import { addDays, daysBetween, isValidDate, weekdayIndex } from '../time/date';
import { FADE_LOOKBACK_MAX_DAYS, LIMITS } from './constants';
import { CheckinRepeat } from './enums';

/**
 * Recurrence rule of a check-in item (docs/requirements/checkin-recurrence-tech-notes.md). Pure
 * functions on `YYYY-MM-DD` personal-day strings: weekdays and day differences come from the date
 * string itself, never from a timestamp or a time zone (time-drift D3, D4, D5, D8).
 */

/** The raw rule fields of a check-in item record; every one may be absent or hold any value. */
export interface CheckinRecurrenceFields {
  readonly active?: unknown;
  readonly repeat?: unknown;
  readonly weekdays?: unknown;
  readonly intervalDays?: unknown;
  readonly startDate?: unknown;
  readonly endDate?: unknown;
  /** Personal date from which a disabled item no longer occurs (data dictionary §3). */
  readonly disabledFrom?: unknown;
}

export const RecurrenceKind = {
  Daily: 'daily',
  Weekly: 'weekly',
  Interval: 'interval',
} as const;
export type RecurrenceKind = (typeof RecurrenceKind)[keyof typeof RecurrenceKind];

/** The effective rule after read-time tolerance (tech notes §3). */
export interface CheckinRecurrence {
  readonly kind: RecurrenceKind;
  /** Ascending, 0 = Monday … 6 = Sunday; non-empty only for `weekly`. */
  readonly weekdays: readonly number[];
  /** 2–365 for `interval`, otherwise null. */
  readonly intervalDays: number | null;
  readonly startDate: string | null;
  readonly endDate: string | null;
}

const readDate = (value: unknown): string | null =>
  typeof value === 'string' && isValidDate(value) ? value : null;

const isIntervalDays = (value: unknown): value is number =>
  typeof value === 'number' &&
  Number.isInteger(value) &&
  value >= LIMITS.checkinIntervalMin &&
  value <= LIMITS.checkinIntervalMax;

/**
 * Interpret the stored fields, never rejecting the record: unknown repeat → daily; invalid
 * weekdays dropped (none left or all 7 → daily); interval with a bad N or without a start date →
 * daily; invalid dates ignored; start after end → both ignored.
 */
export function readCheckinRecurrence(data: CheckinRecurrenceFields): CheckinRecurrence {
  let startDate = readDate(data.startDate);
  let endDate = readDate(data.endDate);
  if (startDate !== null && endDate !== null && startDate > endDate) {
    startDate = null;
    endDate = null;
  }
  const daily: CheckinRecurrence = {
    kind: RecurrenceKind.Daily,
    weekdays: [],
    intervalDays: null,
    startDate,
    endDate,
  };
  if (data.repeat === CheckinRepeat.Weekly) {
    const raw = Array.isArray(data.weekdays) ? (data.weekdays as unknown[]) : [];
    const days = [
      ...new Set(raw.filter((v): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= 6)),
    ].sort((a, b) => a - b);
    if (days.length === 0 || days.length === 7) return daily;
    return { ...daily, kind: RecurrenceKind.Weekly, weekdays: days };
  }
  if (data.repeat === CheckinRepeat.Interval) {
    if (!isIntervalDays(data.intervalDays) || startDate === null) return daily;
    return { ...daily, kind: RecurrenceKind.Interval, intervalDays: data.intervalDays };
  }
  return daily;
}

/** Whether the rule alone (ignoring `active`) makes `personalDate` an occurrence day. */
export function recurrenceOccursOn(rule: CheckinRecurrence, personalDate: string): boolean {
  if (rule.startDate !== null && personalDate < rule.startDate) return false;
  if (rule.endDate !== null && personalDate > rule.endDate) return false;
  if (rule.kind === RecurrenceKind.Weekly) return rule.weekdays.includes(weekdayIndex(personalDate));
  if (rule.kind === RecurrenceKind.Interval) {
    // startDate and intervalDays are non-null for a valid interval rule.
    return daysBetween(rule.startDate as string, personalDate) % (rule.intervalDays as number) === 0;
  }
  return true;
}

/**
 * Whether the item appears on `personalDate` (tech notes §4): the rule hits, and the item is
 * enabled — or disabled, and `personalDate` is before its `disabledFrom`, so past days keep
 * their occurrences, missed ones included (checkin-plan-change.md §3.3). A disabled item without
 * a readable `disabledFrom` (stored before that rule) does not appear.
 */
export function checkinOccursOn(data: CheckinRecurrenceFields, personalDate: string): boolean {
  if (data.active === false) {
    const from = readDate(data.disabledFrom);
    if (from === null || personalDate >= from) return false;
  }
  return recurrenceOccursOn(readCheckinRecurrence(data), personalDate);
}

/**
 * Up to `count` occurrence days before `beforeDate` (exclusive), newest first, not earlier than
 * `floorDate` and not more than FADE_LOOKBACK_MAX_DAYS days back. Every-N-days items do not fade
 * and return an empty list. The caller passes a floor derived from the item's creation.
 */
export function recentOccurrenceDates(
  data: CheckinRecurrenceFields,
  beforeDate: string,
  count: number,
  floorDate: string,
): string[] {
  const rule = readCheckinRecurrence(data);
  if (rule.kind === RecurrenceKind.Interval || data.active === false) return [];
  const windowStart = addDays(beforeDate, -FADE_LOOKBACK_MAX_DAYS);
  const floor = floorDate > windowStart ? floorDate : windowStart;
  const found: string[] = [];
  for (let date = addDays(beforeDate, -1); date >= floor && found.length < count; date = addDays(date, -1)) {
    if (recurrenceOccursOn(rule, date)) found.push(date);
  }
  return found;
}

/**
 * The next `count` occurrence days from `fromDate` (inclusive), ascending, bounded by `endDate`
 * and by a search horizon of 400 days per requested day. Looks at the rule only, so a disabled
 * item still lists its schedule (the form preview).
 */
export function nextOccurrences(data: CheckinRecurrenceFields, fromDate: string, count: number): string[] {
  const rule = readCheckinRecurrence(data);
  const found: string[] = [];
  const horizon = 400 * Math.max(count, 1);
  let date = rule.startDate !== null && rule.startDate > fromDate ? rule.startDate : fromDate;
  for (let i = 0; i < horizon && found.length < count; i += 1, date = addDays(date, 1)) {
    if (rule.endDate !== null && date > rule.endDate) break;
    if (recurrenceOccursOn(rule, date)) found.push(date);
  }
  return found;
}

export const RecurrenceStatus = {
  Active: 'active',
  Ended: 'ended',
  NotStarted: 'notStarted',
} as const;
export type RecurrenceStatus = (typeof RecurrenceStatus)[keyof typeof RecurrenceStatus];

/** Structured summary for the settings list and form; wording belongs to the UI. */
export interface CheckinRecurrenceSummary {
  readonly kind: RecurrenceKind;
  readonly weekdays: readonly number[];
  readonly intervalDays: number | null;
  readonly startDate: string | null;
  readonly endDate: string | null;
  /** `ended`: today is after endDate; `notStarted`: today is before startDate. */
  readonly status: RecurrenceStatus;
  /** First occurrence day from today (inclusive), null when ended or none exists. */
  readonly nextDate: string | null;
}

export function summarizeCheckinRecurrence(
  data: CheckinRecurrenceFields,
  today: string,
): CheckinRecurrenceSummary {
  const rule = readCheckinRecurrence(data);
  const status =
    rule.endDate !== null && today > rule.endDate
      ? RecurrenceStatus.Ended
      : rule.startDate !== null && today < rule.startDate
        ? RecurrenceStatus.NotStarted
        : RecurrenceStatus.Active;
  return {
    kind: rule.kind,
    weekdays: rule.weekdays,
    intervalDays: rule.intervalDays,
    startDate: rule.startDate,
    endDate: rule.endDate,
    status,
    nextDate: status === RecurrenceStatus.Ended ? null : (nextOccurrences(data, today, 1)[0] ?? null),
  };
}

/** The recurrence fields as written by the editor. `undefined`/null mean "not set". */
export interface CheckinRecurrenceInput {
  readonly repeat?: string | null;
  readonly weekdays?: readonly number[] | null;
  readonly intervalDays?: number | null;
  readonly startDate?: string | null;
  readonly endDate?: string | null;
}

/** The five fields as stored: always all present, irrelevant ones null. */
export interface NormalizedCheckinRecurrence {
  readonly repeat: CheckinRepeat | null;
  readonly weekdays: number[] | null;
  readonly intervalDays: number | null;
  readonly startDate: string | null;
  readonly endDate: string | null;
}

export const RecurrenceIssue = {
  UnknownRepeat: 'unknownRepeat',
  WeekdaysEmpty: 'weekdaysEmpty',
  WeekdaysInvalid: 'weekdaysInvalid',
  IntervalOutOfRange: 'intervalOutOfRange',
  StartDateRequired: 'startDateRequired',
  StartDateInvalid: 'startDateInvalid',
  EndDateInvalid: 'endDateInvalid',
  EndBeforeStart: 'endBeforeStart',
} as const;
export type RecurrenceIssue = (typeof RecurrenceIssue)[keyof typeof RecurrenceIssue];

/** Save-time validation (tech notes §2). Empty list = valid. `endDate` before today is allowed. */
export function validateCheckinRecurrence(input: CheckinRecurrenceInput): RecurrenceIssue[] {
  const issues: RecurrenceIssue[] = [];
  const repeat = input.repeat ?? null;
  const startOk = input.startDate == null || readDate(input.startDate) !== null;
  const endOk = input.endDate == null || readDate(input.endDate) !== null;
  if (!startOk) issues.push(RecurrenceIssue.StartDateInvalid);
  if (!endOk) issues.push(RecurrenceIssue.EndDateInvalid);
  if (repeat === CheckinRepeat.Weekly) {
    const days = input.weekdays ?? [];
    if (days.some((d) => !Number.isInteger(d) || d < 0 || d > 6)) issues.push(RecurrenceIssue.WeekdaysInvalid);
    else if (days.length === 0) issues.push(RecurrenceIssue.WeekdaysEmpty);
  } else if (repeat === CheckinRepeat.Interval) {
    if (!isIntervalDays(input.intervalDays)) issues.push(RecurrenceIssue.IntervalOutOfRange);
    if (input.startDate == null) issues.push(RecurrenceIssue.StartDateRequired);
  } else if (repeat !== null) {
    issues.push(RecurrenceIssue.UnknownRepeat);
  }
  if (startOk && endOk && input.startDate != null && input.endDate != null && input.startDate > input.endDate) {
    issues.push(RecurrenceIssue.EndBeforeStart);
  }
  return issues;
}

/**
 * Canonical stored form of a valid input: weekdays deduplicated ascending; seven weekdays become
 * "every day" (repeat and weekdays null); fields irrelevant to the chosen repeat become null;
 * start and end stay independent of the repeat. Call after `validateCheckinRecurrence`.
 */
export function normalizeCheckinRecurrence(input: CheckinRecurrenceInput): NormalizedCheckinRecurrence {
  const base = { startDate: input.startDate ?? null, endDate: input.endDate ?? null };
  if (input.repeat === CheckinRepeat.Weekly) {
    const days = [...new Set(input.weekdays ?? [])].sort((a, b) => a - b);
    if (days.length === 7) return { repeat: null, weekdays: null, intervalDays: null, ...base };
    return { repeat: CheckinRepeat.Weekly, weekdays: days, intervalDays: null, ...base };
  }
  if (input.repeat === CheckinRepeat.Interval) {
    return { repeat: CheckinRepeat.Interval, weekdays: null, intervalDays: input.intervalDays ?? null, ...base };
  }
  return { repeat: null, weekdays: null, intervalDays: null, ...base };
}

/**
 * Same-name check for check-in items: every item takes part, including disabled, ended and
 * not-yet-started ones (tech notes §5). Returns the id of the first other item with the same
 * trimmed name, or null.
 */
export function findDuplicateCheckinName(
  items: readonly { readonly id: string; readonly data: { readonly name: string } | null }[],
  name: string,
  excludeId?: string,
): string | null {
  const wanted = name.trim();
  const hit = items.find((item) => item.id !== excludeId && item.data !== null && item.data.name.trim() === wanted);
  return hit?.id ?? null;
}
